import {
  Activity,
  BookOpen,
  Boxes,
  Building2,
  ChevronRight,
  FileBarChart,
  LayoutDashboard,
  Menu,
  Network,
  Search,
  Settings,
  ShieldCheck,
  Siren,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { navigationItems, type NavigationKey } from "./app/navigation";
import { navigationFromPath, urlForNavigation } from "./app/router";
import { roles, type DemoRole } from "./app/roles";
import { EvidenceDrawer } from "./components/EvidenceDrawer";
import { GuidedWalkthrough } from "./components/GuidedWalkthrough";
import { PlaceholderScreen } from "./components/PlaceholderScreen";
import { PresenterBar } from "./components/PresenterBar";
import { CreateMenu } from "./components/shell/CreateMenu";
import { NotificationCenter } from "./components/shell/NotificationCenter";
import { ProfileMenu } from "./components/shell/ProfileMenu";
import { traceNodes } from "./data/seed";
import { AssetInventory } from "./features/assets/inventory/AssetInventory";
import { PhoneDetail } from "./features/assets/phone-detail/PhoneDetail";
import { IncidentWorkspace } from "./features/incidents/IncidentWorkspace";
import { InfrastructureTrace } from "./features/infrastructure/InfrastructureTrace";
import { OperationsCenter } from "./features/operations/OperationsCenter";
import { IncidentReport } from "./features/reports/IncidentReport";
import { ScenarioProvider, useScenario } from "./simulation/ScenarioContext";
import { RepositoryProvider } from "./local/provider";
import { AudienceProvider, DepthProvider } from "./presentation";

type SearchResult = {
  id: string;
  label: string;
  context: string;
  navigation: NavigationKey;
  nodeId?: string;
};

const navigationIcons: Record<NavigationKey, LucideIcon> = {
  operations: LayoutDashboard,
  incidents: Siren,
  assets: Boxes,
  infrastructure: Network,
  maintenance: Wrench,
  vendors: Building2,
  knowledge: BookOpen,
  reports: FileBarChart,
  administration: Settings,
};

function CrocOpsApp() {
  const [activeNavigation, setActiveNavigation] = useState<NavigationKey>(() =>
    navigationFromPath(window.location.pathname),
  );
  const [viewingAssetId, setViewingAssetId] = useState<string | null>(null);
  const [activeRole, setActiveRole] = useState<DemoRole>("technical-ii");
  const [search, setSearch] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreSheetRef = useRef<HTMLElement>(null);
  const moreCloseRef = useRef<HTMLButtonElement>(null);
  const { selectTraceNode, stage, startScenario } = useScenario();
  const activeRoleDefinition = roles.find((role) => role.id === activeRole) ?? roles[1];

  const handleNavigate = useCallback((key: NavigationKey) => {
    const nextUrl = urlForNavigation(key, window.location.search);
    const nextPath = nextUrl.split("?")[0];
    if (window.location.pathname !== nextPath) {
      window.history.pushState({ navigation: key }, "", nextUrl);
    }
    setActiveNavigation(key);
    setViewingAssetId(null);
    setSearch("");
    setMobileSearchOpen(false);
    setMoreOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const activeItem = useMemo(
    () => navigationItems.find((item) => item.key === activeNavigation) ?? navigationItems[0],
    [activeNavigation],
  );

  useEffect(() => {
    const handlePopState = () => setActiveNavigation(navigationFromPath(window.location.pathname));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    document.title = `CROC-OPS · ${activeItem.label}`;
  }, [activeItem.label]);

  useEffect(() => {
    document.documentElement.dataset.operatorRole = activeRole;
    return () => {
      delete document.documentElement.dataset.operatorRole;
    };
  }, [activeRole]);

  useEffect(() => {
    if (!moreOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    moreCloseRef.current?.focus();
    const handleDialogKeys = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
        return;
      }
      if (event.key !== "Tab" || !moreSheetRef.current) return;
      const focusable = Array.from(
        moreSheetRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href], input, select, textarea"),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleDialogKeys);
    return () => {
      window.removeEventListener("keydown", handleDialogKeys);
      previouslyFocused?.focus();
    };
  }, [moreOpen]);

  const searchResults = useMemo<SearchResult[]>(() => {
    const query = search.trim().toLowerCase();
    if (query.length < 2) return [];
    const recordResults = traceNodes
      .filter((node) => `${node.id} ${node.label} ${node.eyebrow}`.toLowerCase().includes(query))
      .map((node) => ({
        id: node.id,
        label: node.label,
        context: `${node.eyebrow} · ${node.id}`,
        navigation: "infrastructure" as const,
        nodeId: node.id,
      }));
    const routeResults = navigationItems
      .filter((item) => item.label.toLowerCase().includes(query))
      .map((item) => ({
        id: `route-${item.key}`,
        label: item.label,
        context: item.firstBuild ? "Application area" : "Supporting product area",
        navigation: item.key,
      }));
    return [...recordResults, ...routeResults].slice(0, 6);
  }, [search]);

  const openSearchResult = (result: SearchResult) => {
    if (result.nodeId) selectTraceNode(result.nodeId);
    handleNavigate(result.navigation);
  };

  const openCreateDestination = (key: NavigationKey) => {
    if (key === "incidents" && stage === "calm") startScenario(activeRoleDefinition.label);
    handleNavigate(key);
  };

  const openAssetDetails = (assetId: string) => {
    setViewingAssetId(assetId);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const closeAssetDetails = () => {
    setViewingAssetId(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const renderActiveScreen = () => {
    if (viewingAssetId) {
      return <PhoneDetail assetId={viewingAssetId} role={activeRoleDefinition} onBack={closeAssetDetails} />;
    }

    switch (activeNavigation) {
      case "operations":
        return (
          <OperationsCenter
            onOpenAssets={() => handleNavigate("assets")}
            onOpenIncident={() => handleNavigate("incidents")}
            onOpenMaintenance={() => handleNavigate("maintenance")}
            onOpenTrace={() => handleNavigate("infrastructure")}
            role={activeRoleDefinition}
          />
        );
      case "incidents":
        return (
          <IncidentWorkspace
            onOpenReports={() => handleNavigate("reports")}
            onOpenTrace={() => handleNavigate("infrastructure")}
            role={activeRoleDefinition}
          />
        );
      case "assets":
        return <AssetInventory role={activeRoleDefinition} onViewDetails={openAssetDetails} />;
      case "infrastructure":
        return <InfrastructureTrace onOpenIncident={() => handleNavigate("incidents")} role={activeRoleDefinition} />;
      case "reports":
        return <IncidentReport onOpenIncident={() => handleNavigate("incidents")} role={activeRoleDefinition} />;
      default:
        return <PlaceholderScreen item={activeItem} />;
    }
  };

  const mobilePrimary = navigationItems.filter((item) =>
    ["operations", "incidents", "assets", "infrastructure"].includes(item.key),
  );
  const moreItems = navigationItems.filter((item) => !mobilePrimary.some((primary) => primary.key === item.key));

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <div className="app-shell">
        <aside className="navigation-rail">
          <a className="brand" href="https://seasound.space" aria-label="CROC-OPS, a SeaSound product">
            <img className="brand__image" src="/brand/icon-192.png" alt="" />
            <span><strong>CROC-OPS</strong><small>Operations Center</small></span>
          </a>
          <nav aria-label="Primary application navigation">
            {(["primary", "secondary", "administrative"] as const).map((group) => (
              <div className="nav-group" key={group}>
                <span className="nav-group__label">
                  {group === "primary" ? "Operate" : group === "secondary" ? "Understand" : "Configure"}
                </span>
                {navigationItems.filter((item) => item.group === group).map((item) => {
                  const Icon = navigationIcons[item.key];
                  return (
                    <button
                      aria-current={item.key === activeNavigation ? "page" : undefined}
                      className={item.key === activeNavigation ? "nav-item nav-item--active" : "nav-item"}
                      key={item.key}
                      onClick={() => handleNavigate(item.key)}
                      type="button"
                    >
                      <Icon aria-hidden="true" size={19} strokeWidth={1.8} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
          <div className="rail-footer">
            <div><ShieldCheck aria-hidden="true" size={18} /><span><strong>Local-first</strong>Demonstration data</span></div>
            <a href="https://seasound.space">SeaSound ecosystem <ChevronRight aria-hidden="true" size={16} /></a>
          </div>
        </aside>

        <div className="application">
          <header className="command-bar">
            <div className="command-bar__identity">
              <img className="mobile-brand__image" src="/brand/icon-192.png" alt="" />
              <span className="mobile-brand">CROC-OPS</span>
              <strong className="desktop-page-title">{activeItem.label}</strong>
            </div>
            <button
              aria-expanded={mobileSearchOpen}
              aria-label={mobileSearchOpen ? "Close global search" : "Open global search"}
              className="mobile-search-toggle icon-button"
              onClick={() => setMobileSearchOpen((open) => !open)}
              type="button"
            >
              {mobileSearchOpen ? <X aria-hidden="true" /> : <Search aria-hidden="true" />}
            </button>
            <div className={mobileSearchOpen ? "global-search is-open" : "global-search"}>
              <label>
                <span className="sr-only">Global search</span>
                <Search aria-hidden="true" className="global-search__icon" size={18} />
                <input
                  aria-autocomplete="list"
                  aria-controls="global-search-results"
                  aria-expanded={searchResults.length > 0}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search assets, people, ports, rooms, incidents, or phone numbers"
                  value={search}
                />
                <kbd>/</kbd>
              </label>
              {searchResults.length > 0 && (
                <div className="search-results" id="global-search-results" role="listbox">
                  {searchResults.map((result) => (
                    <button key={result.id} onClick={() => openSearchResult(result)} role="option" type="button">
                      <strong>{result.label}</strong>
                      <span>{result.context}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="command-bar__actions">
              <span className={stage !== "calm" ? "environment environment--active" : "environment"}>
                <Activity aria-hidden="true" size={15} />
                {stage !== "calm" ? `Simulation · ${stage}` : "Local demonstration"}
              </span>
              <CreateMenu onNavigate={openCreateDestination} />
              <NotificationCenter onNavigate={handleNavigate} />
              <ProfileMenu onNavigate={handleNavigate} roleLabel={activeRoleDefinition.label} />
            </div>
          </header>

          <PresenterBar
            activeRole={activeRole}
            onNavigate={handleNavigate}
            onRoleChange={setActiveRole}
          />

          <main className="main-content" id="main-content" tabIndex={-1}>
            {renderActiveScreen()}
          </main>

          <nav className="mobile-navigation" aria-label="Mobile application navigation">
            {mobilePrimary.map((item) => {
              const Icon = navigationIcons[item.key];
              return (
                <button
                  aria-current={item.key === activeNavigation ? "page" : undefined}
                  className={item.key === activeNavigation ? "mobile-nav-item mobile-nav-item--active" : "mobile-nav-item"}
                  key={item.key}
                  onClick={() => handleNavigate(item.key)}
                  type="button"
                >
                  <Icon aria-hidden="true" size={20} />
                  <span>{item.key === "operations" ? "Ops" : item.key === "infrastructure" ? "Infra" : item.label}</span>
                </button>
              );
            })}
            <button
              aria-expanded={moreOpen}
              className={moreOpen || moreItems.some((item) => item.key === activeNavigation)
                ? "mobile-nav-item mobile-nav-item--active"
                : "mobile-nav-item"}
              onClick={() => setMoreOpen((open) => !open)}
              type="button"
            >
              <Menu aria-hidden="true" size={20} />
              <span>More</span>
            </button>
          </nav>
        </div>

        {moreOpen && (
          <div className="mobile-sheet-layer">
            <div className="sheet-scrim" aria-hidden="true" onClick={() => setMoreOpen(false)} />
            <section
              aria-label="More navigation"
              aria-modal="true"
              className="mobile-more-sheet"
              ref={moreSheetRef}
              role="dialog"
            >
              <header>
                <div><span className="eyebrow">CROC-OPS</span><h2>More tools</h2></div>
                <button
                  aria-label="Close more navigation"
                  className="icon-button"
                  onClick={() => setMoreOpen(false)}
                  ref={moreCloseRef}
                  type="button"
                >
                  <X aria-hidden="true" />
                </button>
              </header>
              <div className="mobile-more-grid">
                {moreItems.map((item) => {
                  const Icon = navigationIcons[item.key];
                  return (
                    <button key={item.key} onClick={() => handleNavigate(item.key)} type="button">
                      <Icon aria-hidden="true" size={22} />
                      <span><strong>{item.label}</strong><small>{item.firstBuild ? "First-build workflow" : "Supporting product area"}</small></span>
                      <ChevronRight aria-hidden="true" size={18} />
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        <EvidenceDrawer role={activeRoleDefinition} />
        <GuidedWalkthrough onNavigate={handleNavigate} role={activeRoleDefinition} />
      </div>
    </>
  );
}

export function App() {
  return (
    <RepositoryProvider>
      <DepthProvider>
        <AudienceProvider>
          <ScenarioProvider>
            <CrocOpsApp />
          </ScenarioProvider>
        </AudienceProvider>
      </DepthProvider>
    </RepositoryProvider>
  );
}
